'use client';
import { VariantProps } from 'class-variance-authority';
import NextLink, { LinkProps as NextLinkProps } from 'next/link';
import { AnchorHTMLAttributes, HTMLAttributes } from 'react';
import { buttonClassNames } from './Button';

type ButtonVariantProps = VariantProps<typeof buttonClassNames>;

interface Props
    extends
        Omit<NextLinkProps, keyof ButtonVariantProps | 'href'>,
        // `href` is provided by NextLinkProps (typed as Url) — drop the anchor's
        // conflicting string `href` so the two can be extended together.
        Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof ButtonVariantProps | 'href'>,
        ButtonVariantProps {
    /** Optional: a link with nowhere to go yet renders as a disabled button. */
    href?: NextLinkProps['href'];
}

export default function ButtonLink({ href, children, block, size, flat, disabled, className, color, ...props }: Props) {
    const classes = buttonClassNames({
        color,
        block,
        size,
        flat,
        disabled,
        className
    });

    // A disabled link has nowhere to go, and next/link refuses an undefined `href`
    // outright — in development that is a full-page runtime error, not a warning,
    // which is how a draft's greyed-out "Seite ansehen" took the editor down. The
    // same button face as inert text instead: it still says "not yet".
    if (disabled || href === undefined || href === null) {
        // Anchor-only attributes have no meaning on a span.
        const inert: Record<string, unknown> = { ...props };
        delete inert.target;
        delete inert.rel;

        return (
            <span aria-disabled="true" className={classes} {...(inert as HTMLAttributes<HTMLSpanElement>)}>
                {children}
            </span>
        );
    }

    return (
        <NextLink href={href} className={classes} {...props}>
            {children}
        </NextLink>
    );
}

export type { Props as ButtonLinkProps };
